import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";

import { stringToHash } from "../../../utils/hash";
import { getSearchParams, getUpdatedSearchParamsUrl } from "../../../utils/search-params";
import { ProductCard, ProductCardProps } from "../../cards/product-card";
import { Container } from "../../container";
import { PaginationNumbers } from "../../pagination/pagination-numbers";

export interface ProductsGridProps {
  products: Queries.ProductDataFragment[] | null;
  labels: ProductCardProps["labels"] & {
    "product-grid-empty-state": string;
  };
  pageSize?: number;
  isFiltered?: boolean;
}

export const ProductsGrid: FC<ProductsGridProps> = ({
  products,
  labels,
  pageSize = 25,
  isFiltered,
}) => {
  const titleRefs = useRef<Array<HTMLDivElement | null>>([]);

  const [maxTitleHeight, setMaxTitleHeight] = useState(0);

  const onSetMaxTitleHeight = useCallback(() => {
    const maxHeight = Math.max(
      ...titleRefs.current.map((el) => el?.getBoundingClientRect().height ?? 0)
    );
    if (Math.abs(maxHeight) !== Infinity) {
      setMaxTitleHeight(maxHeight);
    }
    return requestAnimationFrame(() => {
      onSetMaxTitleHeight();
    });
  }, []);

  useEffect(() => {
    const requestId = onSetMaxTitleHeight();
    return () => {
      cancelAnimationFrame(requestId);
    };
  }, [onSetMaxTitleHeight]);

  const searchParams = getSearchParams();
  const page = parseInt(searchParams?.get("page") ?? "0");
  const productsCurrentPage = products?.slice(page * pageSize, (page + 1) * pageSize);

  const totalPages = Math.ceil((products?.length ?? 0) / pageSize);
  const hashKey = stringToHash(productsCurrentPage?.map((p) => p.id).join("") ?? "");
  const onSetCurrentPageIndex = useCallback((i: number) => {
    if (typeof window !== "undefined") {
      window.location.href = getUpdatedSearchParamsUrl({ page: i });
    }
  }, []);

  return (
    <AnimatePresence initial={false} mode="popLayout">
      {!products ? null : productsCurrentPage?.length ? (
        <motion.div
          className="h-full flex w-full flex-col justify-stretch"
          key={hashKey}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { ease: "easeOut", duration: 0.5 } }}
        >
          <>
            <div
              className={clsx(
                "grid gap-8 items-center justify-center",
                "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4"
              )}
            >
              {productsCurrentPage.filter(Boolean).map((product, i) => (
                <ProductCard
                  labels={labels}
                  product={product}
                  key={i}
                  titleProps={{
                    style: {
                      height: maxTitleHeight || undefined,
                    },
                  }}
                  isGridFiltered={isFiltered}
                  setTitleRef={(el) => (titleRefs.current[i] = el)}
                />
              ))}
            </div>
            {productsCurrentPage.length !== products.length && (
              <div className="w-full flex justify-center pt-4">
                <PaginationNumbers
                  currentPageIndex={page}
                  getItemHref={(i) => getUpdatedSearchParamsUrl({ page: i })}
                  setCurrentPageIndex={onSetCurrentPageIndex}
                  totalPages={totalPages}
                />
              </div>
            )}
          </>
        </motion.div>
      ) : (
        <Container containerClassName="h-64 flex items-center justify-center">
          <p>{labels["product-grid-empty-state"]}</p>
        </Container>
      )}
    </AnimatePresence>
  );
};
